import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { createCanteen } from "../../../redux/actions/canteenActions";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";

import {
  ArrowLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Building2,
  Send,
  Shield,
  Droplets,
  Eye,
  Stethoscope,
  User,
  Trash2,
  FileText,
  Info,
  Camera,
} from "lucide-react";

const SectionHPersonalHygiene = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();
  const { loading, error: apiError } = useSelector((state) => state.canteen);

  const [form, setForm] = useState({
    h1: "",
    h1Photo: null,
    h1Remarks: "",
    h2: "",
    h2Photo: null,
    h2Remarks: "",
    h3: "",
    h3Remarks: "",
    h4: "",
    h4Remarks: "",
    h5: "",
    h5Document: null,
    h5Remarks: "",
    h6: "",
    h6Remarks: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [previewImages, setPreviewImages] = useState({
    h1Photo: null,
    h2Photo: null,
    h5Document: null,
  });

  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const sectionData = localStorage.getItem("survey_section_h");
        if (sectionData) {
          const savedData = JSON.parse(sectionData);
          setForm(savedData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        const isPartial = determinePartialStatus();
        const dataToSave = {
          ...form,
          isPartial: isPartial,
          lastSaved: new Date().toISOString(),
        };
        localStorage.setItem("survey_section_h", JSON.stringify(dataToSave));
        setSaveStatus(t.global.alert_saved_locally);
        setTimeout(() => setSaveStatus(""), 2000);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [form, t]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      if (!file) return;

      // Validate file size
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({
        ...prev,
        [name]: file,
      }));

      if (file.type.startsWith("image/")) {
        setPreviewImages((prev) => ({
          ...prev,
          [name]: URL.createObjectURL(file),
        }));
      }

      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
        // Clear optional fields when answer changes
        ...(name === "h1" && value === t.global.yes && { h1Remarks: "" }),
        ...(name === "h2" && value === t.global.yes && { h2Remarks: "" }),
        ...(name === "h3" && value === t.global.yes && { h3Remarks: "" }),
        ...(name === "h4" && value === t.global.yes && { h4Remarks: "" }),
        ...(name === "h5" && value === t.global.yes && { h5Remarks: "" }),
        ...(name === "h5" && value === t.global.no && { h5Document: null }),
        ...(name === "h6" && value === t.global.yes && { h6Remarks: "" }),
      }));
    }

    // Clear error when user starts typing/selecting
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Function to handle file removal
  const handleRemoveFile = (fieldName) => {
    setForm((prev) => ({ ...prev, [fieldName]: null }));
  };

  // Determine if section is partially completed
  const determinePartialStatus = () => {
    // Check basic required fields
    if (!form.h1 || !form.h2 || !form.h3 || !form.h4 || !form.h5 || !form.h6) {
      return false;
    }

    // H1: Photo is optional when YES
    if (form.h1 === t.global.yes && !form.h1Photo) {
      return true; // Partial: Missing optional photo
    }

    // H2: Photo is optional when YES
    if (form.h2 === t.global.yes && !form.h2Photo) {
      return true; // Partial: Missing optional photo
    }

    // H5: Document is optional when YES
    if (form.h5 === t.global.yes && !form.h5Document) {
      return true; // Partial: Missing optional document
    }

    return false;
  };

  const validateForm = () => {
    const newErrors = {};

    // H1 validation - Required selection only
    if (!form.h1?.trim()) {
      newErrors.h1 = t.global.err_a1;
    }

    // H2 validation - Required selection only
    if (!form.h2?.trim()) {
      newErrors.h2 = t.global.err_a1;
    }

    // H3 validation - Required selection only
    if (!form.h3?.trim()) {
      newErrors.h3 = t.global.err_a1;
    }

    // H4 validation - Required selection only
    if (!form.h4?.trim()) {
      newErrors.h4 = t.global.err_a1;
    }

    // H5 validation - Required selection only
    if (!form.h5?.trim()) {
      newErrors.h5 = t.global.err_a1;
    }

    // H6 validation - Required selection only
    if (!form.h6?.trim()) {
      newErrors.h6 = t.global.err_a1;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const yesNoToInt = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      // Scroll to first error
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const element = document.querySelector(`[name="${firstErrorField}"]`);
        if (element) {
          element.closest(".question-container")?.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = new FormData();
      payload.append("municipality_id", state?.municipality_id || 1);
      payload.append("ward_id", state?.ward_id || 5);
      payload.append("district_id", state?.district_id || 10);
      payload.append(
        "lattitude_longitude",
        state?.lattitude_longitude || "22.5726,88.3639",
      );
      payload.append("program_name", state?.area_name || "demo kiosk");
      payload.append("current_section", "hygiene");
      payload.append("kiosk_location_fulladdress", state?.fullAddress || "N/A");
      payload.append("reporting_date", new Date().toISOString().split("T")[0]);
      payload.append("area_id", state?.area_id || state?.id);
      payload.append("canteen_type_id", state?.canteen_type || 2);

      // Append form data
      payload.append("hygiene_H1", yesNoToInt(form.h1));
      payload.append("hygiene_H1_remarks", form.h1Remarks || "");
      if (form.h1Photo) {
        payload.append("hygiene_H1_upload", form.h1Photo);
      }

      payload.append("hygiene_H2", yesNoToInt(form.h2));
      payload.append("hygiene_H2_remarks", form.h2Remarks || "");
      if (form.h2Photo) {
        payload.append("hygiene_H2_upload", form.h2Photo);
      }

      payload.append("hygiene_H3", yesNoToInt(form.h3));
      payload.append("hygiene_H3_remarks", form.h3Remarks || "");

      payload.append("hygiene_H4", yesNoToInt(form.h4));
      payload.append("hygiene_H4_remarks", form.h4Remarks || "");

      payload.append("hygiene_H5", yesNoToInt(form.h5));
      payload.append("hygiene_H5_remarks", form.h5Remarks || "");
      if (form.h5Document) {
        payload.append("hygiene_H5_upload", form.h5Document);
      }

      payload.append("hygiene_H6", yesNoToInt(form.h6));
      payload.append("hygiene_H6_remarks", form.h6Remarks || "");

      // Determine partial status
      const isPartial = determinePartialStatus();

      // Save section data with partial flag
      const sectionDataWithPartial = {
        ...form,
        isPartial: isPartial,
        submittedAt: new Date().toISOString(),
        submittedAsPartial: isPartial,
      };

      // Always save the data
      localStorage.setItem(
        "survey_section_h",
        JSON.stringify(sectionDataWithPartial),
      );

      // Update completed sections tracking
      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      if (!isPartial) {
        completedSections.h = true;
      } else {
        delete completedSections.h;
      }
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      // Store partial sections separately
      const partialSections = JSON.parse(
        localStorage.getItem("partialSections") || "{}",
      );
      if (isPartial) {
        partialSections.h = true;
      } else {
        delete partialSections.h;
      }
      localStorage.setItem("partialSections", JSON.stringify(partialSections));

      // Dispatch Redux action
      const response = await dispatch(createCanteen(payload));

      // Navigate back to sections
      navigate("/survey/sections", { state });
    } catch (err) {
      console.error("Error submitting Section H:", err);
      alert(t.global.alert_server_error);
      setIsSubmitting(false);
    }
  };

  const yesNoOptions = [t.global.yes, t.global.no];

  const getSurveyTypeDisplay = (type) => {
    switch (type) {
      case "kiosk":
        return t.global.survey_type_kiosk;
      case "kitchen":
        return t.global.survey_type_kitchen;
      case "kitchencumDining":
        return t.global.survey_type_kitchendining;
      default:
        return type || t.global.survey_type_default;
    }
  };

  // Calculate if form can be submitted
  const isFormSubmittable =
    form.h1 && form.h2 && form.h3 && form.h4 && form.h5 && form.h6;

  // Check if form will be submitted as partial
  const willBePartial = isFormSubmittable ? determinePartialStatus() : false;

  // Show completion status only when form is submittable
  const showCompletionStatus = isFormSubmittable;

  // Helper function to render radio buttons
  const renderQuestion = (
    fieldName,
    label,
    icon,
    showPhotoUpload = false,
    showDocumentUpload = false,
  ) => (
    <div className="question-container border rounded-lg p-4">
      <label className="block font-medium mb-2 flex items-center">
        {icon}
        <span className="ml-2">{label}</span>
        <span className="text-red-500 ml-1">*</span>
      </label>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        {yesNoOptions.map((option) => (
          <label key={option} className="cursor-pointer">
            <div
              className={`border rounded-lg p-4 text-center transition-all duration-200 ${
                form[fieldName] === option
                  ? form[fieldName] === t.global.yes
                    ? "bg-green-50 border-green-300 ring-2 ring-green-100"
                    : "bg-red-50 border-red-300 ring-2 ring-red-100"
                  : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-center">
                <div className="relative mr-2">
                  <input
                    type="radio"
                    name={fieldName}
                    value={option}
                    checked={form[fieldName] === option}
                    onChange={handleChange}
                    className="sr-only"
                    disabled={isSubmitting}
                    required
                  />
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      form[fieldName] === option
                        ? form[fieldName] === t.global.yes
                          ? "border-green-500 bg-green-500"
                          : "border-red-500 bg-red-500"
                        : "border-gray-300"
                    }`}
                  >
                    {form[fieldName] === option && (
                      <div className="w-2 h-2 rounded-full bg-white"></div>
                    )}
                  </div>
                </div>
                <span className="font-medium text-gray-700">{option}</span>
              </div>
            </div>
          </label>
        ))}
      </div>
      {errors[fieldName] && (
        <p className="mt-2 text-sm text-red-600 flex items-center">
          <AlertCircle size={14} className="mr-1" /> {errors[fieldName]}
        </p>
      )}

      {/* Photo Upload for YES answers */}
      {form[fieldName] === t.global.yes && showPhotoUpload && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <label className="block text-sm font-medium mb-4 text-gray-700">
            Upload Evidence ({t.global.optionalNote?.split(":")[0]}):
          </label>

          {/* BUTTONS */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <button
              type="button"
              onClick={() => {
                setCameraField(`${fieldName}Photo`);
                setShowCamera(true);
              }}
              className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-green-800 active:text-white transition-all duration-150"
            >
              <Camera size={20} /> {t.global.use_camera}
            </button>

            <label
              htmlFor={`${fieldName}Photo`}
              className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-green-800 active:text-white transition-all duration-150 cursor-pointer"
            >
              <Upload size={20} /> {t.global.upload_file}
            </label>
          </div>

          <input
            type="file"
            name={`${fieldName}Photo`}
            id={`${fieldName}Photo`}
            onChange={handleChange}
            className="hidden"
            accept="image/*"
            disabled={isSubmitting}
          />

          {/* PREVIEW */}
          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center ${
              form[`${fieldName}Photo`]
                ? "border-yellow-400 bg-yellow-50"
                : "border-gray-300 bg-white"
            }`}
          >
            {form[`${fieldName}Photo`] ? (
              <div className="space-y-4">
                {previewImages?.[`${fieldName}Photo`] && (
                  <img
                    src={previewImages[`${fieldName}Photo`]}
                    alt="Preview"
                    className="max-h-56 mx-auto rounded-lg shadow"
                  />
                )}

                <div className="flex items-center justify-between">
                  <div className="text-left">
                    <p className="text-sm break-all font-medium">
                      {form[`${fieldName}Photo`].name}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => {
                      if (previewImages?.[`${fieldName}Photo`]) {
                        URL.revokeObjectURL(previewImages[`${fieldName}Photo`]);
                      }

                      setForm((prev) => ({
                        ...prev,
                        [`${fieldName}Photo`]: null,
                      }));

                      setPreviewImages((prev) => ({
                        ...prev,
                        [`${fieldName}Photo`]: null,
                      }));
                    }}
                    className="text-red-600 text-xs font-bold"
                  >
                    {t.global.remove}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 italic text-sm">
                {t.global.capture_hint} evidence
              </p>
            )}
          </div>
        </div>
      )}

      {/* Document Upload for YES answers */}
      {form[fieldName] === t.global.yes && showDocumentUpload && (
        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <label className="block text-sm font-medium mb-4 text-gray-700">
            Upload Document ({t.global.optionalNote?.split(":")[0]}):
          </label>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <button
              type="button"
              onClick={() => {
                setCameraField(`${fieldName}Document`);
                setShowCamera(true);
              }}
              className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-green-800 active:text-white transition-all duration-150"
            >
              <Camera size={20} /> {t.global.use_camera}
            </button>

            <label
              htmlFor={`${fieldName}Document`}
              className="flex items-center justify-center gap-2 p-3 border-2 border-gray-300 text-gray-600 rounded-lg active:scale-95 active:bg-green-800 active:text-white transition-all duration-150 cursor-pointer"
            >
              <Upload size={20} /> {t.global.upload_file}
            </label>
          </div>

          <input
            type="file"
            name={`${fieldName}Document`}
            id={`${fieldName}Document`}
            onChange={handleChange}
            className="hidden"
            accept=".pdf,.jpg,.jpeg,.png"
            disabled={isSubmitting}
          />

          <div
            className={`border-2 border-dashed rounded-lg p-6 text-center ${
              form[`${fieldName}Document`]
                ? "border-yellow-400 bg-yellow-50"
                : "border-gray-300 bg-white"
            }`}
          >
            {form[`${fieldName}Document`] ? (
              <div className="space-y-4">
                {previewImages?.[`${fieldName}Document`] &&
                  form[`${fieldName}Document`].type?.startsWith("image/") && (
                    <img
                      src={previewImages[`${fieldName}Document`]}
                      alt="Preview"
                      className="max-h-56 mx-auto rounded-lg shadow"
                    />
                  )}

                <div className="flex items-center justify-between">
                  <p className="text-sm break-all font-medium">
                    {form[`${fieldName}Document`].name}
                  </p>

                  <button
                    type="button"
                    onClick={() => {
                      if (previewImages?.[`${fieldName}Document`]) {
                        URL.revokeObjectURL(
                          previewImages[`${fieldName}Document`],
                        );
                      }

                      setForm((prev) => ({
                        ...prev,
                        [`${fieldName}Document`]: null,
                      }));

                      setPreviewImages((prev) => ({
                        ...prev,
                        [`${fieldName}Document`]: null,
                      }));
                    }}
                    className="text-red-600 text-xs font-bold"
                  >
                    {t.global.remove}
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-gray-400 italic text-sm">
                {t.global.capture_hint} document
              </p>
            )}
          </div>
        </div>
      )}

      {/* Remarks for NO answers */}
      {form[fieldName] === t.global.no && (
        <div className="mt-4">
          <label className="block text-sm font-medium mb-1 text-gray-700">
            {t.global.remarks_label}
            <span className="text-gray-500 ml-1">
              {t.global.optionalNote?.split(":")[0]} - No impact on completion status
            </span>
          </label>
          <textarea
            name={`${fieldName}Remarks`}
            value={form[`${fieldName}Remarks`] || ""}
            onChange={handleChange}
            placeholder={`Please specify why ${label.toLowerCase().replace("?", "")}... (${t.global.optionalNote?.split(":")[0]})`}
            className={`w-full border p-3 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors[`${fieldName}Remarks`]
                ? "border-red-500"
                : "border-gray-300"
            }`}
            rows={3}
            disabled={isSubmitting}
          />
          {errors[`${fieldName}Remarks`] && (
            <p className="mt-2 text-sm text-red-600 flex items-center">
              <AlertCircle size={14} className="mr-1" />{" "}
              {errors[`${fieldName}Remarks`]}
            </p>
          )}
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kiosk.sections.H.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kiosk.sections.H.subtitle}
              </p>
              {saveStatus && (
                <div className="text-sm text-green-600 mt-1 flex items-center">
                  <CheckCircle size={14} className="mr-1" />
                  {saveStatus}
                </div>
              )}
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full flex items-center">
                <Building2 size={14} className="mr-1" />
                {getSurveyTypeDisplay(state?.surveyType)} Survey
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          {/* Partial Submission Warning */}
          {showCompletionStatus && willBePartial && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="text-amber-500 mr-3 mt-0.5" size={20} />
                <div>
                  <p className="font-medium text-amber-800">
                    Partial Submission
                  </p>
                  <p className="text-amber-700 text-sm mt-1">
                    This section will be saved as{" "}
                    <span className="font-semibold">partially completed</span>{" "}
                    because:
                  </p>
                  <ul className="text-amber-700 text-sm mt-2 list-disc pl-5 space-y-1">
                    {form.h1 === t.global.yes && !form.h1Photo && (
                      <li>Photo for H1 is missing ({t.global.optionalNote?.split(":")[0]})</li>
                    )}
                    {form.h2 === t.global.yes && !form.h2Photo && (
                      <li>Photo for H2 is missing ({t.global.optionalNote?.split(":")[0]})</li>
                    )}
                    {form.h5 === t.global.yes && !form.h5Document && (
                      <li>Document for H5 is missing ({t.global.optionalNote?.split(":")[0]})</li>
                    )}
                  </ul>
                  <p className="text-amber-700 text-sm mt-2">
                    You can submit now and complete later, or add the missing
                    optional information for full completion.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* API Error */}
          {apiError && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="text-red-500 mr-3" size={20} />
              <div>
                <p className="font-medium text-red-800">{t.global.error_title}</p>
                <p className="text-red-600 text-sm">{apiError}</p>
              </div>
            </div>
          )}

          <div className="space-y-6">
            {/* H1 - Protective Gear */}
            {renderQuestion(
              "h1",
              t.modules.kiosk.sections.H.questions.protectiveGear,
              <Shield size={18} />,
              true,
              false,
            )}

            {/* H2 - Handwashing Station */}
            {renderQuestion(
              "h2",
              t.modules.kiosk.sections.H.questions.handwashingStation,
              <Droplets size={18} />,
              true,
              false,
            )}

            {/* H3 - Water Stagnation */}
            {renderQuestion(
              "h3",
              t.modules.kiosk.sections.H.questions.waterStagnation,
              <Eye size={18} />,
              false,
              false,
            )}

            {/* H4 - Handwashing Practice */}
            {renderQuestion(
              "h4",
              t.modules.kiosk.sections.H.questions.handwashingPractice,
              <Shield size={18} />,
              false,
              false,
            )}

            {/* H5 - Health Check-ups */}
            {renderQuestion(
              "h5",
              t.modules.kiosk.sections.H.questions.healthCheckups,
              <Stethoscope size={18} />,
              false,
              true,
            )}

            {/* H6 - Personal Hygiene */}
            {renderQuestion(
              "h6",
              t.modules.kiosk.sections.H.questions.personalHygiene,
              <User size={18} />,
              false,
              false,
            )}

            {/* Completion Status - Only show when form is submittable */}
            {showCompletionStatus && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Info size={18} className="mr-2 text-blue-600" />
                    <span className="font-medium text-gray-700">
                      Submission Status:
                    </span>
                  </div>
                  <div>
                    {willBePartial ? (
                      <span className="text-amber-600 font-medium">
                        Will be Partial
                      </span>
                    ) : (
                      <span className="text-green-600 font-medium">
                        Will be Complete
                      </span>
                    )}
                  </div>
                </div>

                {willBePartial && (
                  <div className="mt-2 text-sm text-amber-700">
                    <p>
                      Missing optional photo/document will mark this section as
                      partially completed.
                    </p>
                    <p className="text-xs mt-1">
                      Remarks are completely optional and won't affect
                      completion status.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <div className="flex flex-col items-end">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSubmitting || !isFormSubmittable}
                  className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                    isSubmitting || !isFormSubmittable
                      ? "bg-blue-400 text-white cursor-not-allowed"
                      : willBePartial
                        ? "bg-amber-500 text-white hover:bg-amber-600"
                        : "bg-green-600 text-white hover:bg-green-700"
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      {t.global.submitting}
                    </>
                  ) : (
                    <>
                      <Send size={18} className="mr-2" />
                      {t.global.submit_section_h}
                    </>
                  )}
                </button>

                {isFormSubmittable && willBePartial && (
                  <p className="text-xs text-amber-600 mt-1 text-right">
                    Will be saved as partially completed
                  </p>
                )}
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-600 font-medium">
                  * Required fields
                </span>
                <span className="mx-2">•</span>
                <span className="text-amber-600 font-medium">
                  Optional fields
                </span>
                <span className="mx-2">•</span>
                <span className="text-gray-600">
                  Missing photo/document = Partial completion
                </span>
                <span className="mx-2">•</span>
                <span className="text-gray-500">
                  Remarks are completely optional
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            if (!file || !cameraField) return;

            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            if (file.type.startsWith("image/")) {
              setPreviewImages((prev) => ({
                ...prev,
                [cameraField]: URL.createObjectURL(file),
              }));
            }

            setShowCamera(false);
            setCameraField(null);
          }}
        />
      )}
    </div>
  );
};

export default SectionHPersonalHygiene;